import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useAuth, AuthProvider } from "./contexts/AuthContext";
import { enviarHeartbeat, marcarSessaoInativa } from "./services/authService";
import { api } from "./services/apiService";
import "leaflet/dist/leaflet.css";
import TrainDashboard from "./pages/TrainDashboard";
import CadastroUsuario from "./pages/cadastro/Usuario/CadastroUsuario";
import CadastroPage from "./pages/cadastro/CadastroPage/CadastroPage";
import CadastroTrem from "./pages/cadastro/Trem/CadastroTrem";
import CadastroPerfil from "./pages/cadastro/Perfil/CadastroPerfil";
import Login from "./pages/login/Login";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import Eventos from "./pages/eventos/Eventos";
import TelemetrySearch from "./pages/TelemetrySearch/TelemetrySearch";
import OperadorLinha from "./pages/OperadorLinha/OperadorLinha";
import AlocacoesPage from "./pages/alocacoes/AlocacoesPage";
import UsuariosLogadosPage from "./pages/usuariosLogados/UsuariosLogadosPage";
import NotificacoesPage from "./pages/notificacoes/NotificacoesPage";

import Sidebar from "./components/Sidebar/Sidebar";
import { useMinhaLinhaOperador } from "./app/hooks/useMinhaLinhaOperador";
import CriticalAlertModal from "./components/Modal/CriticalAlertModal";
import useAlarmeStreamMonitor from "./app/hooks/useAlarmeStreamMonitor";

import "./chartjsSetup";
import { ThemeProvider } from "@mui/material/styles";
import { Box } from "@mui/material";
import { theme } from "./theme/theme";
import SoftNotification from "./app/hooks/SoftNotification";
import { useLocation } from "react-router-dom";
function HeartbeatManager() {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;

    enviarHeartbeat();
    const id = setInterval(enviarHeartbeat, 30000);

    return () => clearInterval(id);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    window.addEventListener("pagehide", marcarSessaoInativa);

    return () => window.removeEventListener("pagehide", marcarSessaoInativa);
  }, [isAuthenticated]);

  return null;
}

function AppInner() {
  const location = useLocation();
  const ocultarNotificacao = location.pathname.startsWith("/operador-linha");
  const { isAuthenticated, userInfo } = useAuth();

  const [filaAlarmes, setFilaAlarmes] = useState([]);
  const [erroStreamAlarme, setErroStreamAlarme] = useState(null);

  const { idLinha, loading: linhaLoading } = useMinhaLinhaOperador(userInfo);
  const idLinhaOperador = idLinha;
  const eOperador = userInfo?.roleNome === 'Operador';

  // O backend só emite um alarme quando o parâmetro realmente transiciona de não crítico
  // para crítico (repetições do mesmo alarme já ativo não geram novo evento). Cada evento
  // recebido aqui já é deduplicado por notificationId em useAlarmeStreamMonitor, então basta
  // enfileirar - não há necessidade (e seria incorreto) filtrar por igualdade de valor aqui,
  // já que alarmes de estado usam sempre o mesmo valor (1) a cada nova ativação.
  const processarAlarme = useCallback((alarme) => {
    setFilaAlarmes((prev) => [...prev, alarme]);
  }, []);

  const handleAlarme = useCallback((alarme) => {
    processarAlarme(alarme);
    window.dispatchEvent(new CustomEvent("notificationUpdated", { detail: alarme }));
  }, [processarAlarme]);

  // Um alarme crítico pode estar sendo perdido silenciosamente se o evento vier corrompido -
  // avisa o operador sem bloquear a tela, já que não há um alarme estruturado pra exibir no modal.
  const handleStreamError = useCallback(() => {
    setErroStreamAlarme("Falha ao processar um alarme recebido. Verifique a página de notificações.");
  }, []);

  useAlarmeStreamMonitor(handleAlarme, isAuthenticated, handleStreamError);

  const linhaDisponivel =
    !linhaLoading && idLinhaOperador !== null && idLinhaOperador !== undefined;

  // Operador só vê alarmes da sua linha; admin/sem linha vê todos
  const filaVisivel = linhaDisponivel
    ? filaAlarmes.filter(a => a.linhaId === idLinhaOperador)
    : filaAlarmes;

  const alarmeAtual = filaVisivel.length > 0
    ? {
        notificationId:   filaVisivel[0].notificationId,
        parameterId:      filaVisivel[0].parameterId,
        tremId:           filaVisivel[0].tremId,
        tremNome:         filaVisivel[0].tremNome,
        carro:            filaVisivel[0].carro,
        parameterValue:   filaVisivel[0].parameterValue,
        messageTimestamp: filaVisivel[0].timestamp,
      }
    : null;

  const marcarComoLido = useCallback((alarme) => {
    if (alarme.notificationId && eOperador && idLinhaOperador && Number(alarme.linhaId) === Number(idLinhaOperador)) {
      api.notifications.markAsRead(alarme.notificationId)
        .then(() => window.dispatchEvent(new Event("notificationUpdated")))
        .catch(() => {});
    }
  }, [eOperador, idLinhaOperador]);

  // Fecha apenas o alarme em exibição no popup do admin/sem linha
  const fecharAlarme = () => {
    if (filaVisivel.length === 0) return;
    const alarmeRemover = filaVisivel[0];
    marcarComoLido(alarmeRemover);
    setFilaAlarmes((prev) => {
      const index = prev.indexOf(alarmeRemover);
      return index === -1 ? prev : [...prev.slice(0, index), ...prev.slice(index + 1)];
    });
  };

  // Fecha todos os alarmes atualmente exibidos no modal do operador (único ou lote)
  const fecharAlarmesVisiveis = () => {
    if (filaVisivel.length === 0) return;
    filaVisivel.forEach(marcarComoLido);
    setFilaAlarmes((prev) => prev.filter((a) => !filaVisivel.includes(a)));
  };

  return (
    <>
      <HeartbeatManager />

      <ThemeProvider theme={theme}>

        {/* Operador com linha → modal bloqueante (único alarme ou lote, conforme o que estiver acumulado) */}
        {isAuthenticated && linhaDisponivel && !ocultarNotificacao && filaVisivel.length > 0 && (
          <CriticalAlertModal
            alerts={filaVisivel.length === 1 ? [alarmeAtual] : filaVisivel}
            onClose={fecharAlarmesVisiveis}
          />
        )}

        {/* Admin / sem linha (após loading) → popup de canto */}
        {isAuthenticated && !linhaLoading && !linhaDisponivel && alarmeAtual && !ocultarNotificacao && (
          <SoftNotification
            alert={alarmeAtual}
            onClose={fecharAlarme}
          />
        )}

        {/* Evento de alarme corrompido no stream - avisa sem bloquear, já que não há dados estruturados pro modal */}
        {isAuthenticated && erroStreamAlarme && !ocultarNotificacao && (
          <SoftNotification
            message={erroStreamAlarme}
            severity="error"
            autoHideDuration={6000}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            onClose={() => setErroStreamAlarme(null)}
          />
        )}

        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
          {isAuthenticated && !location.pathname.startsWith('/login') && !location.pathname.startsWith('/operador-linha') && (
            <Sidebar />
          )}
          <Box sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            path="/operador-linha"
            element={
              <ProtectedRoute>
                <OperadorLinha />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <TrainDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/cadastroPage"
            element={
              <ProtectedRoute>
                <CadastroPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/cadastro/trem"
            element={
              <ProtectedRoute>
                <CadastroTrem />
              </ProtectedRoute>
            }
          />

          <Route
            path="/cadastro/usuario"
            element={
              <ProtectedRoute>
                <CadastroUsuario />
              </ProtectedRoute>
            }
          />

          <Route
            path="/cadastro/perfil"
            element={
              <ProtectedRoute>
                <CadastroPerfil />
              </ProtectedRoute>
            }
          />

          <Route
            path="/eventos"
            element={
              <ProtectedRoute>
                <Eventos />
              </ProtectedRoute>
            }
          />

          <Route
            path="/telemetria"
            element={
              <ProtectedRoute>
                <TelemetrySearch />
              </ProtectedRoute>
            }
          />

          <Route
            path="/alocacoes"
            element={
              <ProtectedRoute>
                <AlocacoesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/usuarios-logados"
            element={
              <ProtectedRoute>
                <UsuariosLogadosPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notificacoes"
            element={
              <ProtectedRoute>
                <NotificacoesPage />
              </ProtectedRoute>
            }
          />

          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
          </Box>
        </Box>
      </ThemeProvider>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppInner />
      </Router>
    </AuthProvider>
  );
}

export default App;
